<template>
  <div class="written-off-relation">
    <a-table
      size="small"
      :columns="columns"
      :loading="loading"
      :dataSource="tableData"
      :scroll="{ x: 1500 }"
      :rowKey="(record) => record.id || record.written_off_sn"
      :pagination="false"
    >
      <template slot="no" slot-scope="text, record, index">
        <span>{{ index + 1 }}</span>
      </template>
      <template slot="main_doc_sn" slot-scope="text, record">
        <span class="text-link" @click="jumpSoucePage(record.main_doc_sn, record.main_doc_id)">
          {{ record.main_doc_sn }}
        </span>
      </template>
      <template slot="written_off_amount" slot-scope="text, record">
        <PriceInput :value="text" :currency="record.settlement_currency" :showTxt="true" />
      </template>
      <template slot="action" slot-scope="text, record">
        <OperateBtn
          v-if="$store.getters.hasFunction(record._cancel_code)"
          api="cancelWrittenOff"
          type="danger"
          :text="$t('反核销')"
          :params="{ writtenOffType: record._written_off_doc_type, idList: [record.id] }"
          @success="handleCancelSuccess"
        />
      </template>
      <template slot="expandedRowRender" slot-scope="record">
        <CommonTable
          style="background: #fff"
          :dataSource="record.details || []"
          :columns="detail_columns"
          :showSetting="false"
          :showPagination="false"
          :rowKey="(subRecord) => subRecord.id || subRecord.refer_doc_sn"
        >
          <template v-slot:refer_doc_sn="{ record: subRecord }">
            <span
              class="text-link"
              @click="jumpSoucePage(subRecord.refer_doc_sn, subRecord.refer_doc_id)"
            >
              {{ subRecord.refer_doc_sn }}
            </span>
          </template>
          <template v-slot:written_off_amount="{ text: subText, record: subRecord }">
            <PriceInput
              :showTxt="true"
              :value="subText"
              :currency="subRecord.settlement_currency || record.settlement_currency"
            />
          </template>
        </CommonTable>
      </template>
    </a-table>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import http from '@/service/axios'
import api from '@/service/api'
import PriceInput from '@component/priceInput'
import CommonTable from '@component/commonTable'
import { jumpSoucePage } from '@/common'

export default {
  name: 'WrittenOffRelation',
  components: {
    PriceInput,
    CommonTable,
  },
  props: {
    docSn: {
      type: String,
      default: '',
    },
    businessUnitCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      tableData: [],
    }
  },
  computed: {
    ...mapGetters(['writtenOffTypeMapping']),
    columns() {
      return [
        {
          title: '#',
          align: 'center',
          key: 'NO',
          width: 60,
          scopedSlots: {
            customRender: 'no',
          },
        },
        {
          title: this.$t('核销序号'),
          dataIndex: 'written_off_sn',
          width: 220,
        },
        {
          title: this.$t('核销类型'),
          dataIndex: 'written_off_type',
          width: 120,
          customRender: (text) => this.writtenOffTypeMapping?.[text] || text,
        },
        {
          title: this.$t('核销日期'),
          dataIndex: 'written_off_date',
          width: 120,
        },
        {
          title: this.$t('主单号'),
          dataIndex: 'main_doc_sn',
          width: 200,
          scopedSlots: { customRender: 'main_doc_sn' },
        },
        {
          title: this.$t('核销金额'),
          dataIndex: 'written_off_amount',
          width: 120,
          scopedSlots: { customRender: 'written_off_amount' },
        },
        {
          title: this.$t('结算组织'),
          dataIndex: 'business_unit_name',
          width: 160,
        },
        {
          title: this.$t('客户/供应商'),
          dataIndex: 'partner_name',
          width: 180,
          customRender: (text, record) =>
            record.customer_name ||
            record.payee_name ||
            record.customerName ||
            record.payeeName ||
            '',
        },
        {
          title: this.$t('能否生成凭证'),
          dataIndex: 'need_generate_voucher',
          width: 110,
          customRender: (text, record) => {
            const val = record.needGenerateVoucher ?? record.need_generate_voucher
            return val === 1 ? this.$t('需要') : val === 0 ? this.$t('不需要') : ''
          },
        },
        {
          title: this.$t('是否生成凭证'),
          dataIndex: 'voucher_generated',
          width: 110,
          customRender: (text) => (text ? this.$t('是') : this.$t('否')),
        },
        {
          title: this.$t('凭证号'),
          dataIndex: 'voucher_no',
          width: 120,
        },
        {
          title: this.$t('创建人'),
          dataIndex: 'created_user',
          width: 120,
        },
        {
          title: this.$t('创建时间'),
          dataIndex: 'created_at',
          width: 160,
        },
        {
          title: this.$t('操作'),
          key: 'action',
          width: 100,
          fixed: 'right',
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
    detail_columns() {
      return [
        {
          title: this.$t('关联单号'),
          dataIndex: 'refer_doc_sn',
          width: 200,
          scopedSlots: { customRender: 'refer_doc_sn' },
        },
        {
          title: this.$t('核销金额'),
          dataIndex: 'written_off_amount',
          width: 220,
          scopedSlots: { customRender: 'written_off_amount' },
        },
      ]
    },
  },
  activated() {
    this.queryList()
  },
  watch: {
    docSn: {
      handler(val) {
        if (val && this.businessUnitCode) {
          this.queryList()
        }
      },
      immediate: true,
    },
    businessUnitCode: {
      handler(val) {
        if (val && this.docSn) {
          this.queryList()
        }
      },
    },
  },
  methods: {
    jumpSoucePage,
    handleCancelSuccess() {
      this.queryList()
      this.$emit('success')
    },
    async queryList() {
      if (!this.docSn || !this.businessUnitCode) {
        this.tableData = []
        return
      }
      this.loading = true
      try {
        const p1 = http({
          url: api.getReceivableWrittenOffList,
          data: {
            page_no: 1,
            page_size: 20,
            main_doc_sn: this.docSn,
            businessUnitCode: this.businessUnitCode,
          },
        }).catch(() => null)

        const p2 = http({
          url: api.getReceivableWrittenOffList,
          data: {
            page_no: 1,
            page_size: 20,
            refer_doc_sn: this.docSn,
            businessUnitCode: this.businessUnitCode,
          },
        }).catch(() => null)

        const p3 = http({
          url: api.getPayableWrittenOffList,
          data: {
            page_no: 1,
            page_size: 20,
            main_doc_sn: this.docSn,
            businessUnitCode: this.businessUnitCode,
          },
        }).catch(() => null)

        const p4 = http({
          url: api.getPayableWrittenOffList,
          data: {
            page_no: 1,
            page_size: 20,
            refer_doc_sn: this.docSn,
            businessUnitCode: this.businessUnitCode,
          },
        }).catch(() => null)

        const [res1, res2, res3, res4] = await Promise.all([p1, p2, p3, p4])
        const list1 = res1?.result?.list || []
        const list2 = res2?.result?.list || []
        const list3 = res3?.result?.list || []
        const list4 = res4?.result?.list || []

        // 标记来源：反核销权限码 + 核销单类型（仅 ReceivableWrittenOff / PayableWrittenOff）
        const markSource = (list, cancelCode, writtenOffType) =>
          list.map((item) => ({
            ...item,
            _cancel_code: cancelCode,
            _written_off_doc_type: writtenOffType,
          }))

        const combined = [
          ...markSource(list1, 'receivable_writeoff_cancel', 'ReceivableWrittenOff'),
          ...markSource(list2, 'receivable_writeoff_cancel', 'ReceivableWrittenOff'),
          ...markSource(list3, 'payable_writeoff_cancel', 'PayableWrittenOff'),
          ...markSource(list4, 'payable_writeoff_cancel', 'PayableWrittenOff'),
        ]
        const map = new Map()
        const tableData = []
        combined.forEach((item) => {
          const key = item.id || item.written_off_sn
          if (key && !map.has(key)) {
            map.set(key, true)
            tableData.push(item)
          } else if (!key) {
            tableData.push(item)
          }
        })
        this.tableData = tableData
      } catch (error) {
        console.error('query written off relation error:', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="less" scoped>
.written-off-relation {
  width: 100%;
}
</style>
